import { useEffect, useRef, useState } from "react";
import { Trash2, Upload } from "lucide-react";
import { toast } from "sonner";

import { useAuth } from "../contexts/AuthContext";
import { useTeam } from "../contexts/TeamContext";
import { api } from "../lib/chalkApi";
import {
  ChalkCard, ChalkIllustrationLines, DarkHeader, EmptyState, SectionTitle, WhiteHeader,
} from "../components/chalk/primitives";

const MB = 1024 * 1024;

function formatBytes(n) {
  if (n < 1024) return `${n} B`;
  if (n < MB) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * MB) return `${(n / MB).toFixed(1)} MB`;
  return `${(n / 1024 / MB).toFixed(2)} GB`;
}

// Client-side image compression — Canvas API. Targets ~60-80% reduction.
// Returns the original blob if the file is small or not an image.
async function compressImage(file, maxSize = 1600, quality = 0.82) {
  if (!file.type.startsWith("image/") || file.size < 200 * 1024) return file;
  if (file.type === "image/gif" || file.type === "image/heic" || file.type === "image/heif") return file;
  try {
    const bitmap = await createImageBitmap(file);
    const scale = Math.min(1, maxSize / Math.max(bitmap.width, bitmap.height));
    const w = Math.round(bitmap.width * scale);
    const h = Math.round(bitmap.height * scale);
    const canvas = document.createElement("canvas");
    canvas.width = w; canvas.height = h;
    canvas.getContext("2d").drawImage(bitmap, 0, 0, w, h);
    const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", quality));
    if (!blob) return file;
    return new File([blob], file.name.replace(/\.\w+$/, ".jpg"), { type: "image/jpeg" });
  } catch {
    return file;
  }
}

export default function ChalkLines() {
  const { user } = useAuth();
  const { activeTeam, tier } = useTeam();
  const [files, setFiles] = useState([]);
  const [usage, setUsage] = useState({ used_bytes: 0, cap_bytes: 100 * MB });
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  async function load() {
    if (!activeTeam) return;
    try {
      const data = await api.listFiles(activeTeam.id);
      setFiles(data.files || []);
      setUsage(data.usage || { used_bytes: 0, cap_bytes: 100 * MB });
    } catch (e) { toast.error(e.message); }
  }

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [activeTeam]);

  async function onPick(e) {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const compressed = await compressImage(f);
      await api.uploadFile(activeTeam.id, compressed);
      toast.success(`Added "${f.name}".`);
      load();
    } catch (err) {
      const code = err.code || "";
      const msg = err.message || "Upload failed.";
      if (code === "STORAGE_CAP_REACHED") toast.error(msg);
      else if (code === "FILE_TOO_LARGE") toast.error(msg);
      else if (code === "BAD_FILE_TYPE") toast.error(msg);
      else toast.error(msg);
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  }

  async function remove(f) {
    if (!window.confirm(`Delete ${f.name}?`)) return;
    try {
      await api.deleteFile(activeTeam.id, f.id);
      setFiles((arr) => arr.filter((x) => x.id !== f.id));
      load();
    } catch (e) { toast.error(e.message); }
  }

  const canUpload = user.role !== "guardian";
  const Header = user.role === "guardian" ? WhiteHeader : DarkHeader;
  const usagePct = Math.min(100, Math.round((usage.used_bytes / usage.cap_bytes) * 100));
  const nearCap = usagePct >= 90;

  return (
    <>
      <Header
        eyebrow="Chalk Lines"
        title="Chalk Lines"
        subtitle={activeTeam ? `${activeTeam.name}` : ""}
      />
      <div style={{ padding: "0 18px 24px" }}>
        <SectionTitle label="Storage" />
        <ChalkCard data-testid="storage-meter">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
            <div style={{ fontSize: 15, fontWeight: 500, color: "var(--chalk-board)" }}>
              {formatBytes(usage.used_bytes)} <span style={{ color: "var(--chalk-muted)", fontWeight: 300 }}>of {formatBytes(usage.cap_bytes)}</span>
            </div>
            <div style={{ fontSize: 11, color: nearCap ? "var(--chalk-amber)" : "var(--chalk-muted)", fontWeight: 500 }}>{usagePct}%</div>
          </div>
          <div style={{ marginTop: 8, height: 4, background: "rgba(0,0,0,0.08)", borderRadius: 2, overflow: "hidden" }}>
            <div style={{
              width: `${usagePct}%`, height: "100%",
              background: nearCap ? "var(--chalk-amber)" : "var(--chalk-green)",
              transition: "width 400ms ease",
            }} />
          </div>
          {tier.coach_tier === "pickup" && (
            <div style={{ marginTop: 8, fontSize: 12, color: "var(--chalk-muted)" }}>
              Pickup tops out at {formatBytes(usage.cap_bytes)}. The Chalkboard adds 5 GB.
            </div>
          )}
        </ChalkCard>

        {canUpload && (
          <>
            <SectionTitle label="Add a file" />
            <input
              ref={inputRef}
              type="file"
              accept="image/jpeg,image/png,image/gif,image/heic,image/heif,image/webp"
              data-testid="file-input"
              onChange={onPick}
              style={{ display: "none" }}
            />
            <button
              onClick={() => inputRef.current?.click()}
              className="chalk-btn-ghost"
              disabled={uploading}
              data-testid="upload-btn"
            >
              <Upload size={16} aria-hidden="true" /> {uploading ? "Uploading…" : "Add to Chalk Lines"}
            </button>
            <div style={{ fontSize: 11, color: "var(--chalk-muted)", marginTop: 8 }}>
              JPG, PNG, HEIC, GIF, or WebP. Max 25 MB. Big images are compressed before upload.
            </div>
          </>
        )}

        <SectionTitle label="Filed" />
        {files.length === 0 ? (
          <EmptyState
            icon={<ChalkIllustrationLines />}
            headline="Nothing filed yet."
            body={canUpload ? "Drop a roster sheet, field map, or game photo." : "Files your coach shares will appear here."}
          />
        ) : (
          files.map((f) => (
            <ChalkCard key={f.id} data-testid={`file-${f.id}`} style={{ marginTop: 8 }}>
              <div style={{ display: "flex", justifyContent: "space-between", gap: 10 }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 500, color: "var(--chalk-board)", overflow: "hidden", textOverflow: "ellipsis" }}>{f.name}</div>
                  <div style={{ fontSize: 11, color: "var(--chalk-muted)", marginTop: 2 }}>
                    {formatBytes(f.size_bytes)} · {f.mime}
                  </div>
                </div>
                {canUpload && (
                  <button
                    onClick={() => remove(f)}
                    aria-label={`Delete ${f.name}`}
                    data-testid={`delete-${f.id}`}
                    style={{ background: "transparent", border: "none", color: "var(--chalk-muted)", cursor: "pointer" }}
                  >
                    <Trash2 size={16} aria-hidden="true" />
                  </button>
                )}
              </div>
            </ChalkCard>
          ))
        )}
      </div>
    </>
  );
}
